import React, { useState, useMemo, useCallback } from 'react';
import InputNumberFormat from 'react-number-format';

import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { Refresh as RefreshIcon } from '@styled-icons/boxicons-regular/Refresh';
import { ArrowBoldDown as ArrowBoldDownIcon } from '@styled-icons/entypo/ArrowBoldDown';
import { ArrowBoldUp as ArrowBoldUpIcon } from '@styled-icons/entypo/ArrowBoldUp';
import { Warning as WarningIcon } from '@styled-icons/fluentui-system-filled/Warning';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  FormControl,
  FormLabel,
  Text,
  Grid,
  Divider,
  RadioGroup,
  VStack,
  Radio,
  Input,
  FormErrorMessage,
  Tooltip,
  Icon,
  useMediaQuery,
  Heading,
  Flex,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import { Table, Thead, Tr, Th, Tbody, Td } from 'components/Table';

import useAlert from 'hooks/useAlert';

import { Budget } from 'types/budget';

type FormErrors = {
  [key: string]: string | boolean;
  type: 'invalid' | 'required';
};

interface ModalBudgetGlobalPriceChangeProps extends ModalRootProps {
  data: {
    budget: Budget;
  };
}

type Mode =
  | 'coefficient'
  | 'unitary'
  | 'coefficient_labor'
  | 'unitary_tcu'
  | 'unitary_and_coefficient';
type Change = 'increase' | 'decrease' | 'value';

type PayloadChangeType =
  | 'coefficient'
  | 'labor_coefficient'
  | 'unit_value'
  | 'unit_value_tcu'
  | 'labor_coefficient_and_unit_value';
type PayloadCalculationType = 'increase' | 'discount' | 'value';

const modalMargin = 120;
const modalMobileFix = 100;

const ModalBudgetGlobalPriceChange: React.FC<ModalBudgetGlobalPriceChangeProps> =
  ({ onConfirm, handleClose, data: { budget }, ...restProps }) => {
    const { openAlert } = useAlert();
    const [isMobile] = useMediaQuery('(max-width: 728px)');

    const [mode, setMode] = useState<Mode>('coefficient');
    const [change, setChange] = useState<Change>('increase');
    const [value, setValue] = useState<number | undefined>();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<FormErrors | undefined>();

    type Client = Budget['identification']['client'];
    const clientNameByType = (client: Client): JSX.Element => {
      if (client.type_person === 'legal') {
        return (
          <>
            {client.fantasy_name}
            <br />
            <small>{client.corporate_name}</small>
          </>
        );
      }

      return <>{client.name}</>;
    };

    const changeLabel = useMemo((): string => {
      switch (change) {
        case 'increase':
          return 'Acréscimo (%)';
        case 'decrease':
          return 'Desconto (%)';
        case 'value':
          return 'Novo valor aproximado (R$)';
        default:
          return 'Alteração';
      }
    }, [change]);

    const changeInputsProps = useMemo(() => {
      switch (change) {
        case 'increase':
        case 'decrease':
          return {
            placeholder: '0 %',
            decimalScale: 2,
            fixedDecimalScale: true,
            thousandSeparator: '.',
            decimalSeparator: ',',
            suffix: ' %',
          };
        case 'value':
          return {
            placeholder: 'R$ 0,00',
            decimalScale: 2,
            fixedDecimalScale: true,
            thousandSeparator: '.',
            decimalSeparator: ',',
            prefix: 'R$ ',
          };
        default:
          return {};
      }
    }, [change]);

    const changeIcon = useMemo(() => {
      switch (change) {
        case 'increase':
          return (
            <ArrowBoldUpIcon
              size={16}
              style={{
                marginLeft: -4,
                paddingBottom: 2,
              }}
              color="red"
              title="Acréscimo (%)"
            />
          );
        case 'decrease':
          return (
            <ArrowBoldDownIcon
              size={16}
              style={{
                marginLeft: -4,
                paddingBottom: 2,
              }}
              color="MediumSeaGreen"
              title="Desconto (%)"
            />
          );
        case 'value':
          return (
            <RefreshIcon
              size={18}
              style={{
                paddingBottom: 2,
              }}
              color="blue"
              title="Novo valor aproximado (R$)"
            />
          );
        default:
          return null;
      }
    }, [change]);

    const isAllowedToChangeValue = useMemo(() => {
      if (mode === 'coefficient_labor' || mode === 'unitary_and_coefficient') {
        return false;
      }

      return true;
    }, [mode]);

    const approximateValue = useCallback(
      (n: number, type: 'gross_total' | 'total_bdi' | 'total'): number => {
        switch (change) {
          case 'increase': {
            return n * (1 + Number(value) / 100);
          }
          case 'decrease': {
            const decreased = n * (1 - Number(value) / 100);
            return decreased < 0 ? 0 : decreased;
          }
          case 'value': {
            const difference = Number(value) / budget.total - 1; // percentual de diferença

            if (type === 'gross_total') {
              if (budget.total_bdi === 0) {
                return Number(value);
              }

              return budget.gross_total * (difference + 1);
            }

            if (type === 'total_bdi') {
              if (budget.total_bdi === 0) {
                return 0;
              }

              return budget.total_bdi * (difference + 1);
            }

            if (type === 'total') {
              if (budget.total_bdi === 0) {
                return Number(value);
              }

              return budget.total * (difference + 1);
            }

            return 0;
          }
          default: {
            return 0;
          }
        }
      },
      [change, value, budget],
    );

    const payloadChangeType = useMemo((): PayloadChangeType => {
      switch (mode) {
        case 'coefficient':
          return 'coefficient';
        case 'coefficient_labor':
          return 'labor_coefficient';
        case 'unitary':
          return 'unit_value';
        case 'unitary_tcu':
          return 'unit_value_tcu';
        case 'unitary_and_coefficient':
          return 'labor_coefficient_and_unit_value';
        default:
          return 'coefficient';
      }
    }, [mode]);

    const payloadCalculationType = useMemo((): PayloadCalculationType => {
      switch (change) {
        case 'increase':
          return 'increase';
        case 'decrease':
          return 'discount';
        case 'value':
          return 'value';
        default:
          return 'increase';
      }
    }, [change]);

    const onConfirmSubmit = useCallback(async (): Promise<void> => {
      try {
        setLoading(true);

        await api.post(`/budget/${budget.id}/global/change`, {
          change_type: payloadChangeType,
          calculation_type: payloadCalculationType,
          total: value || 0,
        });

        if (onConfirm) onConfirm();

        toast({
          description: 'Solicitação de alteração realizada com sucesso!',
          status: 'success',
        });

        handleClose();
      } catch (err) {
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao solicitar a alteração!',
          status: 'error',
        });
      } finally {
        setLoading(false);
      }
    }, [
      budget.id,
      handleClose,
      onConfirm,
      payloadCalculationType,
      payloadChangeType,
      value,
    ]);

    const handleConfirm = async (): Promise<void> => {
      const formErrors: FormErrors = {} as FormErrors;

      if (!mode) {
        toast({
          description: 'Selecione a forma de alteração!',
          status: 'warning',
        });

        Object.assign(formErrors, { mode: true, type: 'required' });
      }

      if (!change) {
        toast({
          description: 'Selecione o tipo de alteração!',
          status: 'warning',
        });

        Object.assign(formErrors, { change: true, type: 'required' });
      }

      if (!value) {
        Object.assign(formErrors, { value: true, type: 'required' });
      }

      if (change === 'decrease' && value && value >= 100) {
        toast({
          description: `O valor de desconto não pode ser maior que 100%! O ajuste não pode resultar em valores negativos ou zerados.`,
          status: 'warning',
        });

        Object.assign(formErrors, { value: true, type: 'invalid' });
      }

      if (change === 'value' && value && value <= 0) {
        toast({
          description: `O valor deve ser maior que zero! O ajuste não pode resultar em valores negativos ou zerados.`,
          status: 'warning',
        });

        Object.assign(formErrors, { value: true, type: 'invalid' });
      }

      setErrors(formErrors);
      if (Object.keys(formErrors).length) {
        return;
      }

      openAlert({
        type: 'info',
        size: '3xl',
        title: 'Confirmação de alteração global',
        description: () => (
          <Flex flexDirection="column" width="100%">
            <Text>
              Você está prestes a alterar os valores do orçamento com base nos
              novos parâmetros definidos. Abaixo, confira a prévia do novo custo
              total recalculado:
            </Text>

            <Text fontWeight="bold" mt={1}>
              Esses valores são aproximados e podem sofrer variações devido a
              arredondamentos e cálculos internos do sistema. Serviços com preço
              informado não serão impactados por essa alteração.
            </Text>

            <Table mt={4} variant="striped">
              <Thead>
                <Tr inHeader>
                  <Th>Descrição</Th>
                  <Th>Valor Atual (R$)</Th>
                  <Th>
                    Novo valor (R$)
                    {change === 'value' ? ' ' : ` - ${Number(value)}% `}{' '}
                    {changeIcon}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr inHeader={false}>
                  <Td columnKey={1}>
                    <Text fontWeight="bold">Custo sem BDI</Text>
                  </Td>
                  <Td columnKey={1}>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(budget.gross_total || 0)}
                  </Td>
                  <Td columnKey={1}>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(
                      approximateValue(budget.gross_total, 'gross_total'),
                    )}
                  </Td>
                </Tr>
                <Tr inHeader={false}>
                  <Td columnKey={1}>
                    <Text fontWeight="bold">Custo do BDI</Text>
                  </Td>
                  <Td columnKey={1}>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(budget.total_bdi || 0)}
                  </Td>
                  <Td columnKey={1}>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(approximateValue(budget.total_bdi, 'total_bdi'))}
                  </Td>
                </Tr>
                <Tr inHeader={false}>
                  <Td columnKey={1}>
                    <Text fontWeight="bold">Custo total do orçamento</Text>
                  </Td>
                  <Td columnKey={1}>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(budget.total)}
                  </Td>
                  <Td columnKey={1}>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(approximateValue(budget.total, 'total'))}
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            <Flex
              mt={8}
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={4}
            >
              <Icon as={WarningIcon} w={8} h={8} color="red.500" />
              <Text color="red.500" fontWeight={500}>
                Após a confirmação, não será possível restaurar os preços atuais
                automáticamente. Caso precise reverter as alterações, será
                necessário ajustar os valores manualmente.
              </Text>
            </Flex>
          </Flex>
        ),
        buttons: ['Cancelar', 'Confirmar'],
        onConfirm: onConfirmSubmit,
      });
    };

    return (
      <Modal scrollBehavior="inside" {...restProps}>
        <ModalOverlay />
        <ModalContent
          sx={{
            width: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            maxWidth: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            height: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            maxHeight: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
          }}
        >
          <ModalHeader>Alterar valor global do orçamento</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Heading as="h3" size="md" mb={4} color="gray.800">
              Ao realizar essa ação, todo o orçamento será alterado de acordo
              com os dados atuais, incluindo edições já realizadas. Se a
              alteração for por coeficiente, ela refletirá apenas na estrutura
              das composições (primeiro nível de mapeamento)
            </Heading>

            <Flex
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={4}
            >
              <Icon as={WarningIcon} w={8} h={8} color="orange.500" />
              <Flex flexDirection="column">
                <Text color="gray.800" fontWeight={500}>
                  Para que a alteração global funcione corretamente, todas as
                  bases devem ter a mesma referência. Misturas de bases
                  diferentes ou bases próprias com outras bases podem gerar
                  inconsistências. Certifique-se de que todas as referências são
                  iguais.
                </Text>

                <Text color="gray.800" fontWeight={500}>
                  <strong>Atenção:</strong> A alteração global não impactará
                  serviços com preço informado.
                </Text>
              </Flex>
            </Flex>

            <Divider mt={8} mb={4} />

            <Grid
              w="100%"
              templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
              columnGap={2}
              rowGap={4}
            >
              <FormControl marginTop="4">
                <FormLabel fontWeight="bold">Descrição do orçamento</FormLabel>
                <Text fontStyle="italic">
                  {budget.identification?.description || 'Não especificada'}
                </Text>
              </FormControl>

              {budget.identification?.client && (
                <FormControl marginTop="4">
                  <FormLabel fontWeight="bold">Cliente</FormLabel>
                  <Text fontStyle="italic">
                    {clientNameByType(budget.identification.client)}
                  </Text>
                </FormControl>
              )}
            </Grid>
            <Grid
              w="100%"
              mt={4}
              templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
              columnGap={2}
              rowGap={4}
            >
              <FormControl marginTop="4">
                <FormLabel fontWeight="bold">Total sem BDI</FormLabel>
                <Text fontStyle="italic">
                  {Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(budget.gross_total || 0)}
                </Text>
              </FormControl>

              <FormControl marginTop="4">
                <FormLabel fontWeight="bold">Total do BDI</FormLabel>
                <Text fontStyle="italic">
                  {Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(budget.total_bdi || 0)}{' '}
                  (
                  {Intl.NumberFormat('pt-BR', {
                    maximumFractionDigits: 2,
                  }).format(budget.bdi_percent || 0)}
                  %)
                </Text>
              </FormControl>

              <FormControl marginTop="4">
                <FormLabel fontWeight="bold">Total Geral</FormLabel>
                <Text fontStyle="italic">
                  {Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(budget.total || 0)}
                </Text>
              </FormControl>
            </Grid>

            <Divider mt={8} mb={4} />

            <Grid
              w="100%"
              mt={4}
              templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
              columnGap={12}
              rowGap={4}
            >
              <FormControl marginTop="4">
                <FormLabel fontWeight="bold">Forma de alteração</FormLabel>
                <RadioGroup
                  defaultValue="coefficient"
                  value={mode}
                  onChange={(v) => {
                    setMode(v as Mode);
                    if (
                      v === 'coefficient_labor' ||
                      v === 'unitary_and_coefficient'
                    ) {
                      setChange('increase');
                    }
                  }}
                >
                  <VStack justifyContent="center" alignItems="flex-start">
                    <Radio value="coefficient">
                      Alterar coeficiente{' '}
                      <Tooltip
                        hasArrow
                        label="Altera somente os coeficientes do primeiro nível do mapeamento das composições - insumos e composições auxiliares"
                      >
                        <Icon
                          as={InfoCircleFillIcon}
                          className="icon"
                          color="blue.300"
                        />
                      </Tooltip>
                    </Radio>

                    <Radio value="unitary">
                      Alterar valor unitário{' '}
                      <Tooltip
                        hasArrow
                        label="Ajusta os valores de todos os itens do orçamento, incluindo insumos avulsos"
                      >
                        <Icon
                          as={InfoCircleFillIcon}
                          className="icon"
                          color="blue.300"
                        />
                      </Tooltip>
                    </Radio>

                    <Radio value="coefficient_labor">
                      Alterar coeficiente - mão de obra{' '}
                      <Tooltip
                        hasArrow
                        label="Altera apenas coeficientes de insumos de mão de obra do primeiro nível da composição"
                      >
                        <Icon
                          as={InfoCircleFillIcon}
                          className="icon"
                          color="blue.300"
                        />
                      </Tooltip>
                    </Radio>

                    <Radio value="unitary_tcu">
                      Alterar valor unitário - Acórdão 938/2014 TCU{' '}
                      <Tooltip
                        hasArrow
                        label="A alteração dos preços unitários será aplicada a todos os insumos, exceto aos insumos de mão de obra. Conforme orientação do Acórdão 938/2014 TCU, a modificação dos índices de produtividade só deve ser realizada se for expressamente permitida pelo edital"
                      >
                        <Icon
                          as={InfoCircleFillIcon}
                          className="icon"
                          color="blue.300"
                        />
                      </Tooltip>
                    </Radio>

                    <Radio value="unitary_and_coefficient">
                      Alterar valor e coeficiente{' '}
                      <Tooltip
                        hasArrow
                        label="A alteração dos preços unitários será aplicada a todos os insumos, exceto aos insumos de mão de obra. Altera apenas coeficientes de insumos de mão de obra"
                      >
                        <Icon
                          as={InfoCircleFillIcon}
                          className="icon"
                          color="blue.300"
                        />
                      </Tooltip>
                    </Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              <FormControl marginTop="4" marginLeft={{ base: 0, lg: 16 }}>
                <FormLabel fontWeight="bold">Alteração do valor</FormLabel>
                <RadioGroup
                  defaultValue="increase"
                  value={change}
                  onChange={(v) => setChange(v as Change)}
                >
                  <VStack justifyContent="center" alignItems="flex-start">
                    <Radio value="increase">Acréscimo (%)</Radio>
                    <Radio value="decrease">Desconto (%)</Radio>
                    <Radio
                      value="value"
                      isDisabled={!isAllowedToChangeValue}
                      disabled={!isAllowedToChangeValue}
                      cursor={
                        !isAllowedToChangeValue ? 'not-allowed' : 'pointer'
                      }
                    >
                      Novo valor aproximado (R$){' '}
                    </Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              <FormControl marginTop="4" isInvalid={!!errors?.value}>
                <FormLabel fontWeight="bold">{changeLabel}</FormLabel>
                <InputNumberFormat
                  type="tel"
                  className="text-right"
                  allowNegative={false}
                  displayType="input"
                  {...changeInputsProps}
                  value={value || ''}
                  onValueChange={(v) => {
                    if (v.floatValue) {
                      setErrors((prev) => {
                        if (prev?.value) {
                          return { ...prev, value: false };
                        }

                        return prev;
                      });
                    }

                    setValue(v.floatValue || 0);
                  }}
                  customInput={Input}
                />

                <FormErrorMessage>
                  {!value && errors?.value === true
                    ? 'Valor é obrigatório'
                    : 'Valor inválido'}
                </FormErrorMessage>
              </FormControl>
            </Grid>
          </ModalBody>

          <ModalFooter>
            <Button
              isLoading={loading}
              isDisabled={loading}
              colorScheme="green"
              onClick={handleConfirm}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

export default ModalBudgetGlobalPriceChange;
